import {
  Component,
  OnInit,
  AfterViewInit,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  OnDestroy,
  NgZone,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import {
  GeoJsonFeatureCollection,
  GeoJsonFeature,
  LegendItem,
  GeoJsonPointFeature,
  MarkerOptionsConfig
} from '../../_types/geojson';
import { HttpClient } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { MarkerClusterer } from '@googlemaps/markerclusterer';

declare const google: any;

@Component({
  selector: 'share-geo-map',
  templateUrl: './share-geo-map.component.html',
  styleUrls: ['./share-geo-map.component.scss']
})
export class ShareGeoMapComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  @ViewChild('mapContainer', { static: false }) mapContainer!: ElementRef;

  // core inputs
  @Input() geoJsonUrl: string = '';
  @Input() kmlUrls: string[] = [];
  @Input() points: GeoJsonPointFeature[] = [];

  // view & styling inputs
  @Input() center: google.maps.LatLngLiteral | null = null;
  @Input() zoom: number = 13;
  @Input() height = '70vh';
  // @Input() title: string = 'Coverage Map';
  @Input() legendItems: LegendItem[] = [];

  @Input() geoJsonDataInput: GeoJsonFeatureCollection | null = null;

  @Input() markerOptions: MarkerOptionsConfig = {
    size: 10,
    defaultColors: {
      site: '#2E7D32',
      circuit: '#1976D2',
      node: '#673AB7'
    },
    titleBuilder: (p) => p.name ?? p.id ?? '',
    infoWindowBuilder: (p) =>
      `<div style="font-family: Arial, sans-serif; font-size:12px;">
    <strong>${p.name ?? 'Unknown'}</strong><br/>
    <small>${p.type ?? ''}</small>
    </div>`
  };

  // editing inputs - all default to off, so existing read-only consumers of
  // this component are completely unaffected
  @Input() clickToPlace: boolean = false;

  // when true, the map ignores [center]/[zoom] once real data is available
  // and fits its viewport to the actual points/regions instead. Defaults to
  // off so existing consumers keep their current fixed-center behavior.
  @Input() autoFitBounds: boolean = false;

  // when true, only points inside the current visible viewport (plus a
  // small buffer) get turned into Marker instances - re-evaluated whenever
  // the map settles after a pan/zoom. Needed for point sets in the
  // thousands, where even clustered markers are slow once the user zooms
  // in far enough that clusters break apart into individual pins covering
  // the whole dataset. Defaults to off so existing consumers are unaffected.
  @Input() cullToViewport: boolean = false;

  // Lets a parent that fetches its own data (via [geoJsonDataInput]/[points]
  // rather than this component's own [geoJsonUrl] fetch) keep the loading
  // overlay up until that fetch resolves. Without this, mapReady flips true
  // as soon as the map itself initializes - before the parent's data has
  // arrived - so the overlay disappears too early and markers/regions pop in
  // (and the view jumps as autoFitBounds re-centers) with no indicator.
  // Defaults to false so existing consumers are unaffected.
  @Input() externalLoading: boolean = false;
  // Points sharing the same coordinates (e.g. every customer in one building,
  // or customers placed at their coverage zone's centre) are fanned out in a
  // small golden-angle spiral, a few metres per step, so each one can be seen
  // and hovered when zoomed in. Deterministic: the same input order gives the
  // same positions, so markers don't jump when a live layer refreshes.
  @Input() spreadOverlapping: boolean = true;

  // events
  @Output() featureClick = new EventEmitter<any>();
  @Output() onAreaClick = new EventEmitter<GeoJsonFeature>();
  @Output() onMarkerClick = new EventEmitter<GeoJsonPointFeature>();
  @Output() onMapReady = new EventEmitter<google.maps.Map>();
  @Output() onMapClickLatLng = new EventEmitter<{ lat: number; lng: number }>();
  @Output() onPolygonComplete = new EventEmitter<any>();

  map: google.maps.Map | null = null;
  dataLayer: google.maps.Data | null = null;
  infoWindow: google.maps.InfoWindow | null = null;
  geoJsonData: GeoJsonFeatureCollection | null = null;
  apiLoaded: boolean = false;
  markers: google.maps.Marker[] = [];
  private markerClusterer: MarkerClusterer | null = null;
  private hoverInfoWindow: google.maps.InfoWindow | null = null;
  kmlLayers: google.maps.KmlLayer[] = [];
  drawingPolygon: google.maps.Polygon | null = null;
  isDrawingPolygon: boolean = false;
  private drawingClickListener: google.maps.MapsEventListener | null = null;
  private drawingDblClickListener: google.maps.MapsEventListener | null = null;
  private clickListener: google.maps.MapsEventListener | null = null;
  private idleListener: google.maps.MapsEventListener | null = null;

  private geoSub?: Subscription;
  dataLoading: boolean = false;
  mapReady: boolean = false;
  private destroyed: boolean = false;

  constructor(
    private http: HttpClient,
    private ngZone: NgZone
  ) {}

  ngOnInit(): void {
    if (this.geoJsonDataInput) {
      this.geoJsonData = this.geoJsonDataInput;
    } else if (this.geoJsonUrl) {
      this.loadGeoJsonFromUrl(this.geoJsonUrl);
    }
  }

  ngAfterViewInit(): void {
    // Deferred so initializeMap()'s synchronous mapReady/map-data mutations
    // land in a fresh change-detection cycle rather than the one that just
    // finished checking this view - mutating state inside ngAfterViewInit
    // itself throws NG0100 (ExpressionChangedAfterItHasBeenCheckedError)
    // whenever the Google Maps API is already loaded (so initializeMap runs
    // fully synchronously), which aborts change detection app-wide for that
    // tick.
    setTimeout(() => this.initializeMap());
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.clearMarkers();
    this.clearKmls();
    if (this.dataLayer) {
      this.dataLayer.setMap(null);
      this.dataLayer = null;
    }
    this.stopDrawingPolygon();
    this.clickListener?.remove();
    this.idleListener?.remove();
    this.geoSub?.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['center'] && this.map && this.center) {
      this.map.panTo(this.center);
      this.map.setZoom(this.zoom);
    }

    let shouldFitBounds = false;

    if (changes['points'] && this.mapReady) {
      this.renderPoints();
      shouldFitBounds = true;
    }

    if (changes['geoJsonDataInput']) {
      this.geoJsonData = this.geoJsonDataInput;

      if (this.mapReady && this.geoJsonData) {
        this.addGeoJsonToMap();
        shouldFitBounds = true;
      }
    }

    if (shouldFitBounds) {
      this.fitToData();
    }

    if (changes['geoJsonUrl'] && !this.geoJsonDataInput && this.geoJsonUrl) {
      this.loadGeoJsonFromUrl(this.geoJsonUrl);
    }

    if (changes['clickToPlace'] && !changes['clickToPlace'].firstChange && this.map) {
      this.syncClickToPlaceListener();
    }
  }

  private syncClickToPlaceListener(): void {
    this.clickListener?.remove();
    this.clickListener = null;

    if (!this.clickToPlace || !this.map) return;

    this.clickListener = this.map.addListener('click', (event: google.maps.MapMouseEvent) => {
      if (!event.latLng) return;
      this.ngZone.run(() => {
        this.onMapClickLatLng.emit({ lat: event.latLng!.lat(), lng: event.latLng!.lng() });
      });
    });
  }

  private async initializeMap(): Promise<void> {
    if (!this.mapContainer) return;

    if (typeof google === 'undefined' || !google.maps) {
      const googleMapsReady = (window as any).googleMapsReady;
      if (!googleMapsReady) {
        console.error('Google Maps JavaScript API is not loaded.');
        return;
      }
      // The API script loads async and can still be in flight when this
      // component's view initializes - wait for its callback (wired up in
      // index.html) instead of giving up on the first check.
      await googleMapsReady;
      if (this.destroyed) return;
    }

    if (!this.mapContainer || !this.mapContainer.nativeElement) {
      console.error('Map container not found');
      return;
    }

    this.map = new google.maps.Map(this.mapContainer.nativeElement, {
      center: this.center,
      zoom: this.zoom,
      mapTypeId: google.maps.MapTypeId.ROADMAP
    });

    this.infoWindow = new google.maps.InfoWindow();
    this.mapReady = true;
    this.onMapReady.emit(this.map!);

    this.addKmlLayers();

    if (this.geoJsonData) {
      this.addGeoJsonToMap();
    }

    this.renderPoints();

    this.fitToData();

    this.syncClickToPlaceListener();

    if (this.cullToViewport) {
      this.idleListener = this.map!.addListener('idle', () => {
        this.ngZone.run(() => this.renderPoints());
      });
    }
  }

  /** Fits the map viewport to whatever points/regions are actually loaded,
   * instead of relying on a fixed [center]/[zoom]. Opt-in via autoFitBounds
   * since existing consumers may rely on the current fixed-view behavior. */
  private fitToData(): void {
    if (!this.autoFitBounds || !this.map) return;

    const bounds = new google.maps.LatLngBounds();
    let hasData = false;

    (this.points || []).forEach((p) => {
      const lat = Number(p.latitude);
      const lng = Number(p.longitude);
      if (!isNaN(lat) && !isNaN(lng)) {
        bounds.extend({ lat, lng });
        hasData = true;
      }
    });

    const features = (this.geoJsonData as any)?.features || [];
    features.forEach((feature: any) => {
      const coordinates = feature?.geometry?.coordinates;
      if (coordinates) {
        this.extendBoundsWithCoordinates(bounds, coordinates);
        hasData = true;
      }
    });

    if (hasData && !bounds.isEmpty()) {
      this.map.fitBounds(bounds);
    }
  }

  /** Recursively walks a GeoJSON coordinates array (Point/Polygon/
   * MultiPolygon, arbitrarily nested) and extends bounds with every
   * [lng, lat] pair found. */
  private extendBoundsWithCoordinates(bounds: google.maps.LatLngBounds, coords: any): void {
    if (!Array.isArray(coords)) return;

    if (coords.length >= 2 && typeof coords[0] === 'number' && typeof coords[1] === 'number') {
      bounds.extend({ lat: coords[1], lng: coords[0] });
      return;
    }

    coords.forEach((c) => this.extendBoundsWithCoordinates(bounds, c));
  }

  private addKmlLayers(): void {
    if (!this.map || !this.kmlUrls?.length) return;
    this.clearKmls();

    this.kmlUrls.forEach((url) => {
      try {
        const layer = new google.maps.KmlLayer({
          url,
          map: this.map,
          preserveViewport: true
        });
        this.kmlLayers.push(layer);
      } catch (error) {
        console.error('Error loading KML layer:', error);
      }
    });
  }

  private clearKmls(): void {
    this.kmlLayers.forEach((layer) => layer.setMap(null));
    this.kmlLayers = [];
  }

  private loadGeoJsonFromUrl(url: string): void {
    this.dataLoading = true;
    // load multiple URLs and merge features
    this.geoSub = this.http.get<GeoJsonFeatureCollection>(url).subscribe({
      next: (data) => {
        this.geoJsonData = data;
        this.dataLoading = false;

        this.ngZone.run(() => {
          if (this.map) this.addGeoJsonToMap();
        });
      },
      error: (error) => {
        console.error('Error loading geoJSON from URL:', error);
        this.dataLoading = false;
      }
    });
  }

  private addGeoJsonToMap(): void {
    if (!this.map || !this.geoJsonData) return;

    if (this.dataLayer) {
      this.dataLayer.setMap(null);
      this.dataLayer = null;
    }

    this.dataLayer = new google.maps.Data({ map: this.map });

    try {
      this.dataLayer?.addGeoJson(this.geoJsonData as any);
    } catch (err) {
      console.error('Error adding geoJSON to map:', err);
    }

    this.dataLayer?.setStyle((feature: google.maps.Data.Feature) => {
      const color = this.getFeatureProperty(feature, 'color') || '#84499D';
      const strokeColor = this.getFeatureProperty(feature, 'strokeColor') || color;
      return {
        fillColor: color,
        fillOpacity: 0.5,
        strokeColor,
        strokeWeight: 2,
        clickable: true
      } as google.maps.Data.StyleOptions;
    });

    this.dataLayer?.addListener('click', (event: google.maps.Data.MouseEvent) => {
      this.ngZone.run(() => {
        this.handleAreaClick(event);
      });
    });

    this.dataLayer?.addListener('mouseover', (event: google.maps.Data.MouseEvent) => {
      this.dataLayer?.overrideStyle(event.feature, { fillOpacity: 0.75, strokeWeight: 3 });
    });

    this.dataLayer?.addListener('mouseout', (event: google.maps.Data.MouseEvent) => {
      this.dataLayer?.revertStyle();
    });
  }

  handleAreaClick(event: google.maps.Data.MouseEvent) {
    if (!this.map) return;

    const feature = event.feature;
    const geometry = feature.getGeometry();
    if (!geometry) return;

    const bounds = new google.maps.LatLngBounds();

    if (geometry.getType() === 'Polygon') {
      const polygon = geometry as google.maps.Data.Polygon;
      const firstPath = polygon.getAt(0);
      for (let i = 0; i < firstPath.getLength(); i++) {
        bounds.extend(firstPath.getAt(i));
      }
    } else if (geometry.getType() === 'MultiPolygon') {
      const multi = geometry as google.maps.Data.MultiPolygon;
      multi.getArray().forEach((polygon: google.maps.Data.Polygon) => {
        const firstPath = polygon.getAt(0);
        for (let i = 0; i < firstPath.getLength(); i++) bounds.extend(firstPath.getAt(i));
      });
    }

    const center = bounds.getCenter();

    // 🔑 Read properties directly
    const name = feature.getProperty('name') ?? 'Unnamed Area';
    const capacity = feature.getProperty('capacity') ?? 'N/A';

    const uptakeLabel = feature.getProperty('uptake_label');
    const content = `
      <div style="font-family: Arial; font-size: 14px">
        <strong>${name}</strong><br/>
        ${uptakeLabel ? `Uptake: ${uptakeLabel}` : `Capacity: ${capacity}`}
      </div>
    `;

    this.infoWindow?.close();
    this.infoWindow?.setContent(content);
    this.infoWindow?.setPosition(center);
    this.infoWindow?.open(this.map);

    this.onAreaClick.emit({ name, capacity, rawFeature: feature } as any);
  }

  /**
   * Renders points as clustered markers rather than one-marker-per-point.
   * With large point sets (e.g. a few thousand imported addresses) creating
   * that many individual Marker + InfoWindow instances directly on the map
   * is what actually makes the map sluggish - clustering keeps the number
   * of on-map overlays small until the user zooms in, and a single shared
   * InfoWindow (instead of one per marker) avoids instantiating thousands
   * of them up front.
   */
  /** With cullToViewport on, only returns points within the current visible
   * bounds (padded by ~30% so markers don't pop in right at the edge while
   * panning). Falls back to the full list otherwise, or if bounds aren't
   * available yet (e.g. right at map init). */
  private getPointsToRender(): GeoJsonPointFeature[] {
    if (!this.cullToViewport || !this.map) return this.points;

    const bounds = this.map.getBounds();
    if (!bounds) return this.points;

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    const latPad = (ne.lat() - sw.lat()) * 0.3;
    const lngPad = (ne.lng() - sw.lng()) * 0.3;
    const paddedBounds = new google.maps.LatLngBounds(
      { lat: sw.lat() - latPad, lng: sw.lng() - lngPad },
      { lat: ne.lat() + latPad, lng: ne.lng() + lngPad }
    );

    return this.points.filter((p) => {
      const lat = Number(p.latitude);
      const lng = Number(p.longitude);
      return !isNaN(lat) && !isNaN(lng) && paddedBounds.contains({ lat, lng });
    });
  }

  /** One position per point; coincident points (same coordinates to ~1 m)
   * after the first are placed on a spiral around the shared spot. */
  private spreadPositions(points: GeoJsonPointFeature[]): ({ lat: number; lng: number } | null)[] {
    const STEP_METRES = 6;
    const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));
    const seen = new Map<string, number>();
    return points.map((p) => {
      const lat = Number(p.latitude);
      const lng = Number(p.longitude);
      if (isNaN(lat) || isNaN(lng)) return null;
      if (!this.spreadOverlapping) return { lat, lng };
      const key = `${lat.toFixed(5)},${lng.toFixed(5)}`;
      const n = seen.get(key) ?? 0;
      seen.set(key, n + 1);
      if (n === 0) return { lat, lng };
      const radius = STEP_METRES * Math.sqrt(n);
      const angle = n * GOLDEN_ANGLE;
      const dLat = (radius * Math.cos(angle)) / 111320;
      const dLng = (radius * Math.sin(angle)) / (111320 * Math.max(Math.cos((lat * Math.PI) / 180), 0.01));
      return { lat: lat + dLat, lng: lng + dLng };
    });
  }

  private renderPoints(): void {
    if (!this.map) return;
    this.clearMarkers();

    if (!this.points?.length) return;

    const pointsToRender = this.getPointsToRender();
    if (!pointsToRender.length) return;

    if (!this.hoverInfoWindow) {
      this.hoverInfoWindow = new google.maps.InfoWindow();
    }

    const defaultColors = this.markerOptions.defaultColors ?? {
      site: '#2E7D32',
      circuit: '#1976D2'
    };
    const baseSize = this.markerOptions.size ?? 10;

    const positions = this.spreadPositions(pointsToRender);

    pointsToRender.forEach((p, index) => {
      // validate coords
      const position = positions[index];
      if (!position) return;
      const { lat, lng } = position;

      const color = p.iconColor ?? defaultColors[p.type ?? 'node'] ?? '#333';
      const scale = this.scaleForType(p.type, baseSize);

      const marker = new google.maps.Marker({
        position: { lat, lng },
        title: this.markerOptions.titleBuilder ? this.markerOptions.titleBuilder(p) : p.name,
        zIndex: p.type === 'site' ? 10 : p.type === 'circuit' ? 5 : 8,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale,
          fillColor: color,
          fillOpacity: 0.9,
          strokeColor: '#FFFFFF',
          strokeWeight: 1.5
        }
      });
      // Stashed so the cluster renderer below can colour a cluster bubble
      // the same as the nodes it contains, rather than the library default.
      (marker as any).__nodeColor = color;

      marker.addListener('mouseover', () => {
        const hoverContent = this.markerOptions.infoWindowBuilder
          ? this.markerOptions.infoWindowBuilder(p)
          : `<div>${p.name ?? ''}${(p as any).uptake_label ? '<br/><small>' + (p as any).uptake_label + '</small>' : ''}</div>`;
        this.hoverInfoWindow!.setContent(hoverContent);
        this.hoverInfoWindow!.open(this.map, marker);
      });
      marker.addListener('mouseout', () => this.hoverInfoWindow!.close());

      marker.addListener('click', () => {
        this.onMarkerClick.emit(p);
      });

      this.markers.push(marker);
    });

    this.markerClusterer = new MarkerClusterer({
      map: this.map,
      markers: this.markers,
      renderer: { render: (cluster) => this.renderCluster(cluster) }
    });
  }

  /**
   * Colours a cluster bubble using the most common node colour among the
   * markers it contains, instead of the clusterer library's fixed red/blue
   * default - same circular-with-halo look as DefaultRenderer, different
   * fill. Falls back to the plain marker colour default if a marker somehow
   * wasn't tagged.
   */
  private renderCluster(cluster: { count: number; position: any; markers: any[] }): google.maps.Marker {
    const { count, position, markers } = cluster;

    const colorCounts = new Map<string, number>();
    (markers ?? []).forEach((m) => {
      const c = (m as any).__nodeColor ?? '#673AB7';
      colorCounts.set(c, (colorCounts.get(c) ?? 0) + 1);
    });
    let color = '#673AB7';
    let max = 0;
    colorCounts.forEach((n, c) => {
      if (n > max) {
        max = n;
        color = c;
      }
    });

    const svg = window.btoa(`
      <svg fill="${color}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240">
        <circle cx="120" cy="120" opacity=".6" r="70" />
        <circle cx="120" cy="120" opacity=".3" r="90" />
        <circle cx="120" cy="120" opacity=".2" r="110" />
        <circle cx="120" cy="120" opacity=".1" r="130" />
      </svg>`);

    return new google.maps.Marker({
      position,
      icon: {
        url: `data:image/svg+xml;base64,${svg}`,
        scaledSize: new google.maps.Size(45, 45)
      },
      label: {
        text: String(count),
        color: 'rgba(255,255,255,0.9)',
        fontSize: '12px'
      },
      zIndex: 1000 + count
    });
  }

  private clearMarkers(): void {
    this.markerClusterer?.clearMarkers();
    this.markerClusterer = null;
    this.markers = [];
  }

  private scaleForType(type: string | undefined, base: number): number {
    if (!type) return base;
    if (type === 'site') return Math.round(base * 1.2);
    if (type === 'circuit') return Math.round(base * 0.8);
    if (type === 'node') return Math.round(base * 1.0);
    return base;
  }

  private getFeatureProperty(feature: google.maps.Data.Feature, key: string): any {
    // Google Data Feature exposes properties at top-level keys (if geojson properties are converted),
    // but sometimes geojson is loaded such that properties are nested under 'properties'
    const direct = feature.getProperty(key);
    if (direct !== undefined) return direct;
    const nested = feature.getProperty('properties');
    if (
      nested &&
      typeof nested === 'object' &&
      (nested as Record<string, any>)[key] !== undefined
    ) {
      return (nested as Record<string, any>)[key];
    }
    return undefined;
  }

  private extractFeatureProperties(feature: google.maps.Data.Feature): any {
    const props: Record<string, any> = feature.getProperty('properties') ?? {};
    // If top-level properties exist, merge them
    (Object.keys(feature as any) || []).forEach(() => {});
    // also copy known keys
    const maybeName = feature.getProperty('name');
    if (maybeName) props['name'] = maybeName;
    // attempt to return the parseable props object
    return props;
  }

  public setCenter(lat: number, lng: number, zoom?: number): void {
    if (!this.map) return;
    this.map.panTo({ lat, lng });
    if (zoom) this.map.setZoom(zoom);
  }

  public refreshPoints(points: GeoJsonPointFeature[]): void {
    this.points = points;
    this.renderPoints();
  }

  public refreshGeoJson(data: GeoJsonFeatureCollection): void {
    this.geoJsonData = data;
    this.addGeoJsonToMap();
  }

  /**
   * Starts manual polygon drawing (the Maps JS API's DrawingManager was
   * removed by Google as of v3.65 - see
   * https://developers.google.com/maps/deprecations). Each map click adds a
   * vertex; a double-click finishes the shape. Emits the drawn shape as
   * GeoJSON via onPolygonComplete, then removes the temporary overlay - the
   * caller is expected to persist it and it will reappear once
   * geoJsonDataInput refreshes from the backend.
   */
  public startDrawingPolygon(): void {
    if (!this.map) {
      console.error('Map is not initialized.');
      return;
    }

    this.stopDrawingPolygon();
    this.isDrawingPolygon = true;

    this.drawingPolygon = new google.maps.Polygon({
      map: this.map,
      paths: [[]],
      fillColor: '#84499D',
      fillOpacity: 0.4,
      strokeColor: '#84499D',
      strokeWeight: 2,
      editable: false,
      clickable: false
    });

    this.drawingClickListener = this.map.addListener(
      'click',
      (event: google.maps.MapMouseEvent) => {
        if (!event.latLng || !this.drawingPolygon) return;
        this.ngZone.run(() => {
          const path = this.drawingPolygon!.getPath();
          path.push(event.latLng!);
        });
      }
    );

    this.drawingDblClickListener = this.map.addListener(
      'dblclick',
      (event: google.maps.MapMouseEvent) => {
        this.ngZone.run(() => {
          // A double-click registers as two 'click' events before 'dblclick'
          // fires, so the final vertex is a duplicate of the one before it.
          const path = this.drawingPolygon?.getPath();
          if (path && path.getLength() > 1) {
            path.pop();
          }
          this.finishDrawingPolygon();
        });
      }
    );
  }

  /** Finalizes the in-progress polygon and emits it via onPolygonComplete. */
  public finishDrawingPolygon(): void {
    if (!this.drawingPolygon) return;

    const path = this.drawingPolygon.getPath();

    if (path.getLength() < 3) {
      this.stopDrawingPolygon();
      return;
    }

    const coordinates: number[][] = [];
    for (let i = 0; i < path.getLength(); i++) {
      const point = path.getAt(i);
      coordinates.push([point.lng(), point.lat()]);
    }
    coordinates.push(coordinates[0]);

    this.onPolygonComplete.emit({
      type: 'Polygon',
      coordinates: [coordinates]
    });

    this.stopDrawingPolygon();
  }

  public stopDrawingPolygon(): void {
    this.isDrawingPolygon = false;
    this.drawingClickListener?.remove();
    this.drawingClickListener = null;
    this.drawingDblClickListener?.remove();
    this.drawingDblClickListener = null;
    if (this.drawingPolygon) {
      this.drawingPolygon.setMap(null);
      this.drawingPolygon = null;
    }
  }
}
